import React from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  LightMode, 
  DarkMode, 
  Palette, 
  AutoAwesome 
} from '@mui/icons-material';
import { useTheme } from '../../theme/ThemeProvider';
import { ThemeMode } from '../../theme/theme';

export interface ThemeSelectorProps {
  /**
   * Whether to show as dropdown selector or icon buttons
   */
  variant?: 'dropdown' | 'icons';
  /**
   * Label for the dropdown (only applies to dropdown variant)
   */
  label?: string;
  /**
   * Size of the component
   */
  size?: 'small' | 'medium';
  /**
   * Controlled value (optional - if not provided, uses internal theme context)
   */
  value?: ThemeMode;
  /**
   * Controlled onChange handler (optional - if not provided, uses internal theme context)
   */
  onChange?: (mode: ThemeMode) => void;
}

const themeOptions = [
  { value: 'light', label: 'Light', icon: <LightMode /> },
  { value: 'dark', label: 'Dark', icon: <DarkMode /> },
  { value: 'neumorphism-light', label: 'Neumorphism Light', icon: <Palette /> },
  { value: 'neumorphism-dark', label: 'Neumorphism Dark', icon: <AutoAwesome /> },
] as const;

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({
  variant = 'dropdown',
  label = 'Theme',
  size = 'medium',
  value,
  onChange,
}) => {
  // Wrap useTheme in try-catch to handle cases where ThemeProvider is not available
  let themeContext;
  try {
    themeContext = useTheme();
  } catch (error) {
    console.error('ThemeSelector: useTheme hook failed. Make sure ThemeSelector is wrapped in ThemeProvider:', error);
    return (
      <Box p={2} border={1} borderColor="error.main" borderRadius={1}>
        Error: ThemeSelector must be used within a ThemeProvider
      </Box>
    );
  }

  // Use controlled props if provided, otherwise use context
  const currentMode = value !== undefined ? value : themeContext.mode;
  const handleThemeChange = onChange || themeContext.setTheme;

  const handleChange = (newMode: ThemeMode) => {
    try {
      handleThemeChange(newMode);
    } catch (error) {
      console.error('ThemeSelector: Failed to set theme:', error);
    }
  };

  if (variant === 'icons') {
    return (
      <Box display="flex" gap={0.5}>
        {themeOptions.map((option) => (
          <Tooltip key={option.value} title={option.label} arrow>
            <IconButton
              size={size}
              onClick={() => handleChange(option.value as ThemeMode)}
              color={currentMode === option.value ? 'primary' : 'default'}
              sx={{
                backgroundColor: currentMode === option.value ? 'action.selected' : 'transparent',
              }}
            >
              {option.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>
    );
  }

  return (
    <FormControl size={size} sx={{ minWidth: 180 }}>
      <InputLabel id="theme-selector-label">{label}</InputLabel>
      <Select
        labelId="theme-selector-label"
        value={currentMode}
        label={label}
        onChange={(event) => handleChange(event.target.value as ThemeMode)}
      >
        {themeOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Box display="flex" alignItems="center" gap={1}>
              {option.icon}
              {option.label}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}; 